import AppBuilderBaseLayout from '@/components/layout/AppBuilderBaseLayout';
import {
    BlockStack,
    Box,
    Button,
    Card,
    Divider,
    Grid,
    Icon,
    InlineStack,
    Link,
    ProgressBar,
    SkeletonBodyText,
    Text,
    TextField,
    Tooltip,
} from '@shopify/polaris';
import { useEffect, useState } from 'react';
import { ArrowLeftIcon, ClipboardIcon, EditIcon, XCircleIcon } from '@shopify/polaris-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import {
    analyticsEventStore,
    buildMobileAppStore,
    defaultMobileAppConfigStore,
    mobileAppConfigStore,
    shopStore,
} from '@/stores';
import { mobileRoutes, vfsRoutes } from '@/constants/routes.constants';
import setUpNewCustomeraccountsstep1 from '../../assets/images/setUpNewCustomeraccountsstep1.gif';
import setUpNewCustomeraccountsstep2 from '../../assets/images/setUpNewCustomeraccountsstep2.gif';
import setUpNewCustomeraccountsstep3 from '../../assets/images/setUpNewCustomeraccountsstep3.gif';
import setUpNewCustomeraccountsstep4 from '../../assets/images/setUpNewCustomeraccountsstep4.gif';
import VfsModal from '../../v2/Components/VfsModal';

const SetupCustomerAccountLogin = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { isAppRequestSubmitted, iosLink, androidLink, isAppConfigLoading } =
        useSnapshot(buildMobileAppStore);
    const defaultMobileAppConfigSnapshot = useSnapshot(
        defaultMobileAppConfigStore,
        {
            sync: true,
        },
    );
    const { customerAccountClientId } = defaultMobileAppConfigSnapshot;

    const [headlessClientId, setHeadlessClientId] = useState('');
    const [headlessClientIdErr, setHeadlessClientIdErr] = useState('');
    const [selected, setSelected] = useState<number>(0);
    const [isCOnfirmClientIdModalOpen, setIsConfirmClientIdModalOpen] =
        useState(false);
    const [isSavingId, setIsSavingId] = useState(false);
    const [isInfoLoading, setIsInfoLoading] = useState(false);

    const shopDomain = shopStore.primaryDomainUrl != '' ? shopStore.primaryDomainUrl : `https://${shopStore.shopifyDomain}`;

    const getStepTitle = () => {
        switch (currentStep) {
            case 1:
                return 'Step 1: Get the Client ID from Shopify’s Headless App';

            case 2:
                return 'Step 2: Add Your Store’s Callback URI';

            case 3:
                return 'Step 3: Connect the Client ID in Videeo Mobile App';

            case 4:
                return 'Step 4: Enable Customer Accounts Login in Shopify';

            default:
                break;
        }
    };

    const changeStep = (action: string) => {
        if (
            (action == 'previous' && currentStep == 1) ||
            (action == 'next' && currentStep == 4)
        )
            return;

        if (currentStep == 3) {
            setHeadlessClientIdErr('');
            setHeadlessClientId('');
        }
        setCurrentStep(action == 'next' ? currentStep + 1 : currentStep - 1);
    };

    const handleSaveButtonClick = () => {
        if (!isValidShpId(headlessClientId)) {
            return;
        } else {
            setIsConfirmClientIdModalOpen(true);
        }
    };

    const isValidShpId = (input: string) => {
        if (input === '') {
            setHeadlessClientIdErr('Please enter a valid Customer Account API Client ID');
            return false;
        } else {
            setHeadlessClientIdErr('');
            return true;
        }
    };

    const handlePasteId = async () => {
        try {
            const text = await navigator.clipboard.readText();
            if (isValidShpId(text.toString())) {
                setHeadlessClientId(text);
                setHeadlessClientIdErr('');
            }
        } catch (err) {
            setHeadlessClientIdErr('Please enter a valid Customer Account API Client ID');
        }
    };

    const fetchCustomerAccountClientId = async () => {
        try {
            setIsInfoLoading(true);
            const result = await fetch('/api/shopify/shop-details', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!result.ok) {
                throw new Error('Failed to fetch get started status');
            }

            const { data } = await result.json();            
            mobileAppConfigStore.customerAccountClientId =
                data?.shop?.customerAccountClientId || null;
            defaultMobileAppConfigStore.customerAccountClientId =
                data?.shop?.customerAccountClientId || null;
        } catch (error) {
            console.log(error);
        } finally {
            setIsInfoLoading(false);
        }
    }

    const storeHeadlessClientId = async () => {
        setIsSavingId(true);
        const resp = await shopStore.storeHeadlessClientId(headlessClientId);
        if (resp) {
            analyticsEventStore.setupCustomerAccountLoginStepsEvents(
                headlessClientId,
                `//*[@id="app"]/div[1]/div/div/div/div/div/div[3]/div/div[3]/button[2]`,
            );
            setHeadlessClientIdErr('');
            setHeadlessClientId('');
            setCurrentStep(4);
            setIsSavingId(false);
            setIsConfirmClientIdModalOpen(false);
        }
    };

    const handleDoneButtonClick = () => {
        navigate(vfsRoutes.landing);
    };

    const handleCopyButtonClicked = async () => {
        try {
            await navigator.clipboard.writeText(shopDomain);
            shopify.toast.show('Copied domain name.', {
                isError: false,
                duration: 5000,
            });
        } catch (err) {
            shopify.toast.show('Failed to copy', {
                isError: true,
                duration: 5000,
            });
        }
    };

    useEffect(() => {
        if (customerAccountClientId) {
            setHeadlessClientId(customerAccountClientId);
        }
    }, [customerAccountClientId]);

    useEffect(() => {
        fetchCustomerAccountClientId();
        window.scrollTo(0, 0);
        if (pathname.includes(mobileRoutes.setup_customer_account_login)) {
            setSelected(
                isAppRequestSubmitted && (iosLink || androidLink) ? 3 : 4,
            );
        }
    }, []);

    const step1Section = () => {
        return (
            <ol className="order-list">
                <li>
                    Open your{' '}
                    <Text as="span" fontWeight="bold">
                        Shopify Admin
                    </Text>{' '}
                    and navigate to the{' '}
                    <Link url="https://apps.shopify.com/headless">
                        Headless
                    </Link>{' '}
                    app.
                </li>
                <li>
                    Click{' '}
                    <Text as="span" fontWeight="bold">
                        Manage
                    </Text>{' '}
                    next to the{' '}
                    <Text as="span" fontWeight="bold">
                        Customer Account API
                    </Text>{' '}
                    section.
                </li>
                <li>
                    Copy the{' '}
                    <Text as="span" fontWeight="bold">
                        Client ID
                    </Text>{' '}
                    displayed on the screen.
                </li>
            </ol>
        );
    };

    const step2Section = () => {
        return (
            <ol className="order-list">
                <li>
                    Scroll down to the{' '}
                    <Text as="span" fontWeight="bold">
                        Application setup
                    </Text>{' '}
                    section.
                </li>
                <li>
                    <InlineStack gap="100" blockAlign="center" align="start">
                        <Text as="p">Click on </Text>
                        <Text as="span" fontWeight="bold">
                            Edit
                        </Text>
                        <Box>
                            <Icon source={EditIcon} tone="base" />
                        </Box>{' '}
                        <Text as="p"> to modify the section.</Text>
                    </InlineStack>
                </li>
                <li>
                    <BlockStack gap="200">
                        <Text as="p">
                            In the{' '}
                            <Text as="span" fontWeight="bold">
                                Callback URI(s)
                            </Text>{' '}
                            field, enter your store’s Shopify domain.
                        </Text>
                        <InlineStack
                            gap="200"
                            blockAlign="center"
                            align="start"
                        >
                            <Box
                                background="bg-fill-secondary-hover"
                                padding="100"
                                borderRadius="200"
                                width="295px"
                            >
                                <Text as="span" tone="subdued">
                                    {shopDomain}
                                </Text>
                            </Box>
                            <Button
                                size="micro"
                                icon={ClipboardIcon}
                                onClick={() => handleCopyButtonClicked()}
                            />
                        </InlineStack>
                    </BlockStack>
                </li>
            </ol>
        );
    };

    const step3Section = () => {
        return (
            <ol className="order-list">
                <li>
                    <BlockStack gap="200">
                        <Tooltip content="You can find this in the Headless app > Customer Account API > Manage">
                            <Box width="340px">
                                <TextField
                                    autoComplete="off"
                                    label="Client ID"
                                    placeholder="Paste your Shopify Headless App Client ID here"
                                    maxLength={40}
                                    value={headlessClientId}
                                    onChange={(value) => {setHeadlessClientId(value.trim())}}
                                    error={headlessClientIdErr}
                                    size="medium"
                                    onClearButtonClick={() => {
                                        setHeadlessClientId('');
                                        setHeadlessClientIdErr('');
                                    }}
                                />
                            </Box>
                        </Tooltip>
                        <Box>
                            {headlessClientId == '' ? (
                                <Button
                                    variant="primary"
                                    icon={ClipboardIcon}
                                    onClick={handlePasteId}
                                >
                                    Paste
                                </Button>
                            ) : (
                                <InlineStack
                                    gap="200"
                                    blockAlign="center"
                                    align="start"
                                >
                                    <Button
                                        variant="primary"
                                        icon={XCircleIcon}
                                        onClick={() => {
                                            setHeadlessClientId('');
                                            setHeadlessClientIdErr('');
                                        }}
                                    >
                                        Clear
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={handleSaveButtonClick}
                                    >
                                        Save
                                    </Button>
                                </InlineStack>
                            )}
                        </Box>
                    </BlockStack>
                </li>
            </ol>
        );
    };

    const step4Section = () => {
        return (
            <ol className="order-list">
                <li>
                    Go to{' '}
                    <Text as="span" fontWeight="bold">
                        Settings
                    </Text>{' '}
                    on your Shopify dashboard.
                </li>
                <li>
                    Select{' '}
                    <Text as="span" fontWeight="bold">
                        Customer accounts
                    </Text>{' '}
                    from the Settings menu.
                </li>
                <li>
                    Enable{' '}
                    <Text as="span" fontWeight="bold">
                        Show login links
                    </Text>{' '}
                    toggle.
                </li>
                <li>
                    Select the{' '}
                    <Text as="span" fontWeight="bold">
                        Customer accounts
                    </Text>{' '}
                    version.
                </li>
            </ol>
        );
    };

    const custumerIdSetupSteps = (
        <Card padding="400">
            <BlockStack gap="400">
                <InlineStack
                    wrap={false}
                    align="space-between"
                    blockAlign="center"
                >
                    <InlineStack
                        wrap={false}
                        align="start"
                        blockAlign="center"
                        gap="400"
                    >
                        <Button
                            icon={ArrowLeftIcon}
                            onClick={() => changeStep('previous')}
                        />
                        <BlockStack gap="200">
                            <div
                                style={{
                                    fontFamily: 'Plus Jakarta Sans',
                                    fontWeight: 700,
                                    fontSize: '18px',
                                    lineHeight: '32px',
                                    letterSpacing: '-0.2px',
                                }}
                            >
                                Complete Step-by-Step Setup for Customer
                                Accounts Login
                            </div>
                            <Box width="40rem">
                                <Text as="p" variant="bodyMd" tone="subdued">
                                    This pre-requisite step needs to be
                                    completed on your Shopify Dashboard, only
                                    once to setup the 'Customer Accounts'. You
                                    will not be needed to repeat this step
                                    during regular Videeo usage.
                                </Text>
                            </Box>
                        </BlockStack>
                    </InlineStack>
                    <InlineStack wrap={false} align="center" gap="200">
                        <div
                            style={{
                                fontFamily: 'Plus Jakarta Sans',
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: '20px',
                                letterSpacing: '0px',
                            }}
                        >
                            Step {currentStep} of 4
                        </div>
                        <div style={{ width: 100 }}>
                            <ProgressBar
                                animated
                                progress={(currentStep / 4) * 100}
                                size="small"
                            />
                        </div>
                    </InlineStack>
                </InlineStack>

                <Divider />

                <Box
                    borderRadius="400"
                    borderWidth="025"
                    borderColor="border-disabled"
                    padding="400"
                >
                    <BlockStack gap="600">
                        <Text as="h5" variant="headingMd">
                            {' '}
                            {getStepTitle()}{' '}
                        </Text>
                        <Grid>
                            <Grid.Cell
                                columnSpan={{
                                    xs: 3,
                                    sm: 3,
                                    md: 3,
                                    lg: 6,
                                    xl: 6,
                                }}
                            >
                                {currentStep == 1 && step1Section()}
                                {currentStep == 2 && step2Section()}
                                {currentStep == 3 && step3Section()}
                                {currentStep == 4 && step4Section()}
                            </Grid.Cell>
                            <Grid.Cell
                                columnSpan={{
                                    xs: 3,
                                    sm: 3,
                                    md: 3,
                                    lg: 6,
                                    xl: 6,
                                }}
                            >
                                <img
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                    }}
                                    src={
                                        currentStep == 1
                                            ? setUpNewCustomeraccountsstep1
                                            : currentStep == 2
                                              ? setUpNewCustomeraccountsstep2
                                              : currentStep == 3
                                                ? setUpNewCustomeraccountsstep3
                                                : currentStep == 4
                                                  ? setUpNewCustomeraccountsstep4
                                                  : ''
                                    }
                                    alt="poclips2kCarousel"
                                />
                            </Grid.Cell>
                        </Grid>
                        <Divider />
                        <BlockStack inlineAlign="end">
                            <InlineStack gap="400">
                                <Button
                                    variant="plain"
                                    onClick={() => changeStep('previous')}
                                >
                                    Previous
                                </Button>
                                {currentStep != 4 && (
                                    <Button onClick={() => changeStep('next')}>
                                        Next
                                    </Button>
                                )}
                                {currentStep == 4 && (
                                    <Button onClick={handleDoneButtonClick}>
                                        Done
                                    </Button>
                                )}
                            </InlineStack>
                        </BlockStack>
                    </BlockStack>
                </Box>
            </BlockStack>
        </Card>
    );

    const editCustumerIdFlow = (
        <Card padding="800">
            <BlockStack gap="400">
                <InlineStack gap="400" blockAlign="end" align="space-between">
                    <div
                        style={{
                            fontFamily: 'Plus Jakarta Sans',
                            fontWeight: 700,
                            fontSize: '18px',
                            lineHeight: '32px',
                            letterSpacing: '-0.2px',
                        }}
                    >
                        Client ID
                    </div>
                </InlineStack>
                <Text as="p" variant="bodyMd" tone="subdued">
                    ✅ That’s it! You’ve successfully completed setting up the
                    Customer Accounts Login on your mobile app.
                </Text>
                <Text as="p">
                    Current Client ID:{' '}
                    <Text as="span" fontWeight="bold">
                        {customerAccountClientId}
                    </Text>
                </Text>
                <Text as="p">
                    If you face any issue with your app, please reach out to{' '}
                    <Link url="mailto:support@videeo.live">Support</Link>.
                </Text>
            </BlockStack>
        </Card>
    );

    return (
        <AppBuilderBaseLayout
            title="Setup Headless app"
            subtitle={''}
            buttonLabel=""
            selectedTabId={selected}
        >
            <BlockStack inlineAlign="center">
                <Box width="60rem">
                    {isAppConfigLoading || isInfoLoading ? (
                        <Card padding="400">
                            <SkeletonBodyText />
                        </Card>
                    ) : customerAccountClientId ? (
                        editCustumerIdFlow
                    ) : (
                        custumerIdSetupSteps
                    )}
                </Box>
            </BlockStack>

            {isCOnfirmClientIdModalOpen && (
                <VfsModal
                    title="Confirm Client ID"
                    modalWidth="30rem"
                    onCloseModal={() => setIsConfirmClientIdModalOpen(false)}
                    primaryActionText={isSavingId ? 'Saving...' : 'Save'}
                    primaryAction={storeHeadlessClientId}
                    secondaryActionText="Canel"
                    secondaryAction={() => setIsConfirmClientIdModalOpen(false)}
                    crossIcon={false}
                    closeable={false}
                    btnFullWidth
                >
                    <Text as="p" variant="bodyMd">
                        Please confirm your updated Client ID:
                        <Text as="span" fontWeight="bold">
                            {' '}
                            {headlessClientId}
                        </Text>
                        .
                    </Text>
                </VfsModal>
            )}
        </AppBuilderBaseLayout>
    );
};

export default SetupCustomerAccountLogin;
